import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Search, Users, Check, X } from 'lucide-react';

interface SupplierItem {
  supplierId: string;
  supplierName: string;
  category: string;
  contactEmail: string;
  rating: string;
}

const REGISTERED_SUPPLIERS: SupplierItem[] = [
  { supplierId: 'VND-LENOVO-01', supplierName: 'Lenovo Enterprise Solutions Ltd', category: 'IT Hardware OEM', contactEmail: 'bids@lenovo-enterprise.co.in', rating: 'A+' },
  { supplierId: 'VND-DELL-02', supplierName: 'Dell Global Commercial India Pvt Ltd', category: 'IT Hardware OEM', contactEmail: 'enterprise.quotes@dell.com', rating: 'A+' },
  { supplierId: 'VND-HP-03', supplierName: 'HP India Sales Pvt Ltd', category: 'IT Hardware OEM', contactEmail: 'sourcing@hp.com', rating: 'A' },
  { supplierId: 'VND-COMPASS-04', supplierName: 'Compass IT Systems Ltd', category: 'IT Distributor', contactEmail: 'bids@compass-it.in', rating: 'B+' },
  { supplierId: 'VND-VOLTAS-09', supplierName: 'Voltas Electro-Mechanical Solutions', category: 'HVAC Manufacturer', contactEmail: 'commercials@voltas.com', rating: 'A+' },
  { supplierId: 'VND-BLUESTAR-10', supplierName: 'Blue Star Cooling Ltd', category: 'HVAC Manufacturer', contactEmail: 'industrial@bluestar.in', rating: 'A' },
  { supplierId: 'VND-CARRIER-11', supplierName: 'Carrier India HVAC Corp', category: 'HVAC OEM', contactEmail: 'quotes@carrier.in', rating: 'A+' },
];

interface RfqSupplierModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirmInvite: (selectedSuppliers: SupplierItem[]) => Promise<void>;
  alreadyInvitedIds?: string[];
  isSubmitting?: boolean;
}

export const RfqSupplierModal: React.FC<RfqSupplierModalProps> = ({
  isOpen,
  onClose,
  onConfirmInvite,
  alreadyInvitedIds = [],
  isSubmitting = false,
}) => {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && !isSubmitting) onClose();
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleKeyDown);
      };
    } else {
      document.body.style.overflow = '';
      return undefined;
    }
  }, [isOpen, isSubmitting, onClose]);

  if (!isOpen) return null;

  const filtered = REGISTERED_SUPPLIERS.filter(
    (s) =>
      s.supplierName.toLowerCase().includes(search.toLowerCase()) ||
      s.category.toLowerCase().includes(search.toLowerCase()) ||
      s.supplierId.toLowerCase().includes(search.toLowerCase())
  );

  const toggleSelect = (id: string) => {
    if (alreadyInvitedIds.includes(id)) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const handleConfirm = async () => {
    const list = REGISTERED_SUPPLIERS.filter((s) => selectedIds.includes(s.supplierId));
    await onConfirmInvite(list);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 font-heading">
      <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-md animate-in fade-in" onClick={() => !isSubmitting && onClose()} />

      <div className="relative z-50 w-full max-w-xl rounded-2xl border border-border bg-surface dark:bg-[#0e101f] text-foreground p-6 shadow-2xl space-y-4 animate-in zoom-in-95">
        <div className="flex items-start justify-between border-b border-border pb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-xl bg-primary/10 text-primary border border-primary/20">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-extrabold text-base text-foreground">Select Enterprise Suppliers for RFQ Package</h3>
              <p className="text-xs text-foreground-secondary font-medium">Search vendor master database and issue invitations.</p>
            </div>
          </div>

          <Button variant="ghost" size="sm" onClick={onClose} disabled={isSubmitting} className="h-8 w-8 p-0 rounded-full">
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-foreground-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search vendor name, category, supplier ID..."
            className="w-full rounded-xl border border-border bg-background pl-9 pr-4 py-2 text-xs font-semibold text-foreground focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* Supplier Item List */}
        <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
          {filtered.map((sup) => {
            const isAlreadyInvited = alreadyInvitedIds.includes(sup.supplierId);
            const isSelected = selectedIds.includes(sup.supplierId);

            return (
              <div
                key={sup.supplierId}
                onClick={() => toggleSelect(sup.supplierId)}
                className={`p-3 rounded-xl border flex items-center justify-between text-xs cursor-pointer transition-all ${
                  isAlreadyInvited
                    ? 'border-border bg-muted/40 opacity-60 cursor-not-allowed'
                    : isSelected
                    ? 'border-primary bg-primary/10 ring-1 ring-primary'
                    : 'border-border bg-card hover:border-primary/40'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`h-4 w-4 rounded border flex items-center justify-center ${
                      isSelected || isAlreadyInvited ? 'bg-primary text-white border-primary' : 'border-border'
                    }`}
                  >
                    {(isSelected || isAlreadyInvited) && <Check className="h-3 w-3" />}
                  </div>

                  <div>
                    <span className="font-extrabold text-foreground block">{sup.supplierName}</span>
                    <span className="text-[11px] text-foreground-secondary block">
                      {sup.category} • Contact: <span className="font-mono">{sup.contactEmail}</span>
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="font-mono text-[10px] font-extrabold text-purple-600 bg-purple-500/10 px-2 py-0.5 rounded">
                    Rating: {sup.rating}
                  </span>
                  {isAlreadyInvited && <span className="text-[10px] font-bold text-foreground-muted">Already Invited</span>}
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-border">
          <span className="text-xs font-bold text-primary">{selectedIds.length} Supplier(s) Selected</span>

          <div className="flex items-center gap-3">
            <Button variant="outline" disabled={isSubmitting} onClick={onClose} className="rounded-xl font-bold">
              Cancel
            </Button>
            <Button
              disabled={isSubmitting || selectedIds.length === 0}
              onClick={handleConfirm}
              className="rounded-xl font-bold bg-primary text-white"
            >
              {isSubmitting ? 'Transmitting Invitations...' : `Transmit ${selectedIds.length} Invitations`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
